// W20 DataTable (legacy BaseTable over q-table, on TanStack Table): header row 12 px / 500 capitalised on the gray
// alternate, 44 px with its rules inside, body text 13 px, rows 44 px with their rule inside (B1226), with a 6 % hover on every cell, 30 rows a page with the 30 / 50 / All picker only past 30 rows, the footer
// "Displaying {from}-{to} of {total} Records", first/last pager buttons only past two pages. W22 ChipIndicator.
import { Fragment, useState, type ReactNode } from 'react';
import {
  flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type ExpandedState, type SortingState,
} from '@tanstack/react-table';
import { backofficeMetrics } from '@radish/ui-tokens';
import { SelectField } from './choice.tsx';

/** Column layout hints (`columnDef.meta`): alignment and width, as legacy columns set them. */
interface ColumnLayout {
  readonly align?: 'left' | 'center' | 'right';
  readonly width?: string;
  /**
   * Legacy's `ui-icon-column-left` (_utils.scss: `padding: 0 0 0 4px !important`) on a column legacy pins at `width: 15px`: the
   * cell holds its icon 4 px in and the column is as narrow as it (Order Lookup's receipt, 32 px; B1224).
   */
  readonly iconLeft?: boolean;
  /**
   * Legacy's `@click` on a whole q-td (Prep Times' name, StorePrepTimes.vue:136-139; B1245): a press anywhere in the cell, its
   * padding and the row's height included, opens the row. The cell keeps a button inside it as the keyboard's focus target;
   * a press on that button (or any control in the cell) is left to it, so the row opens once.
   */
  onCellClick?(row: unknown): void;
}

const layoutOf = (meta: unknown): ColumnLayout | undefined => meta as ColumnLayout | undefined;

/** Whether a press inside a cell landed on a control of its own (a button, a link, a field), which handles it itself. */
const pressedControl = (target: EventTarget, cell: Element): boolean => {
  const control = target instanceof Element ? target.closest('button, a, input, select, textarea, [role="button"]') : null;
  return control !== null && cell.contains(control);
};

/**
 * Quasar's sort icon (QTh, `.q-table__sort-icon`; B637): Material's `arrow_upward` at 120 % of the 12 px header (14.4 px),
 * always in the layout so auto-width columns are as wide as legacy's. Hidden until the header is hovered (.64), shown at
 * .86 when sorted, and turned over for descending. Left and centre columns carry it after the label (4 px gap), right
 * columns before it, so a money column's label still ends at the figures' edge. Exported for hand-built q-table headers
 * (the Breaks report, B657).
 */
export function SortIcon({ sorted, align }: { readonly sorted: false | 'asc' | 'desc'; readonly align: 'left' | 'center' | 'right' | undefined }) {
  return (
    <svg
      viewBox="0 0 24 24" width={14.4} height={14.4} aria-hidden data-testid="sort-icon"
      className={`inline-block shrink-0 align-middle fill-current transition-transform duration-300 ease-[cubic-bezier(.25,.8,.5,1)] ${align === 'right' ? 'mr-1' : 'ml-1'} ${sorted === false ? 'opacity-0 group-hover:opacity-[.64]' : 'opacity-[.86]'} ${sorted === 'desc' ? 'rotate-180' : ''}`}
    >
      <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
    </svg>
  );
}

const alignClass = (align: 'left' | 'center' | 'right' | undefined): string =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

interface DataTableProps<T> {
  readonly columns: ColumnDef<T, never>[];
  readonly data: readonly T[];
  readonly getRowId: (row: T) => string;
  readonly initialSort?: SortingState;
  readonly onRowClick?: (row: T) => void;
  /**
   * With onRowClick, each row is also focusable and Enter or Space opens it, as legacy's `q-tr tabindex="0"` with
   * `@keydown.enter` and `@keydown.space.prevent` (the customer's Orders tab, B1288). Off by default.
   */
  readonly rowKeys?: boolean;
  /**
   * What shows instead of the table when no row is visible. Without it, a table with no Total row draws legacy BaseTable's
   * own no-data block ({@link TableNoData}: no header, no bar), given `noDataIllustration` and `noDataTerm` (B1325).
   */
  readonly empty?: ReactNode;
  /** Legacy BaseTable's `no-data-icon` / `no-data-icon-dark`, for the default no-data block. */
  readonly noDataIllustration?: NoDataIllustration;
  /** Legacy BaseTable's `filter`: a search with no match says "No results found" / Nothing matches “term” (B1325). */
  readonly noDataTerm?: string;
  /** A totals row under the body (reports), aligned with the columns. */
  readonly totals?: readonly ReactNode[] | undefined;
  /**
   * With no row visible, keep the totals over the no-data block as legacy BaseTable's `bottom-row` slot does (QTable draws
   * it whatever the rows; BaseTable only hides the header): a header-less row whose leading blank cells are one colspan
   * cell and whose figures sit in `ui-icon-column-left` cells, right-aligned in a `px-4 py-2` box (the drawer's Orders and
   * Payments, B1446). Off by default: a Total row otherwise keeps the whole table (B1323).
   */
  readonly noDataTotals?: boolean;
  /** Hides the paging footer (reports show every row). */
  readonly showAll?: boolean;
  readonly testId?: string;
  readonly caption?: string;
  /**
   * Detail rows under an expanded row (legacy q-table `props.expand`: the item list's variants), one cell per column.
   * A row with none can't expand; a cell toggles its row with `row.toggleExpanded()`.
   */
  readonly subRows?: (row: T) => readonly (readonly ReactNode[])[];
  /**
   * Server paging (legacy BaseTableServerSide): `data` is the page the server answered, the footer counts `total`, and
   * the pager and Records per page ask `onPage` for another page instead of slicing `data`.
   */
  readonly serverPaging?: ServerPaging;
  /**
   * The legacy screen is a BaseTableServerSide (ProductsList, OrderLookupList, CustomerOrdersTab and the bulk assigns),
   * whose Records per page picker is 60 px; BaseTable's is 50 (B1366). A table with `serverPaging` is one already.
   */
  readonly legacyServerSide?: boolean;
  /**
   * Tint a row under the pointer (legacy q-tr's hover). Off by default: legacy's lists and reports draw their rows as
   * `q-tr no-hover` (B1265). On where legacy's rows hover: Dining Sales, Sales Consolidated, the customer's Orders tab and
   * the employee detail tabs (q-tables with no row slot).
   */
  readonly rowHover?: boolean;
  /** Tint a detail row under the pointer: legacy's variant rows are plain q-tr (the item list, Item Sales; B1265). */
  readonly subRowHover?: boolean;
  /**
   * Let header, body, detail and totals cells wrap (legacy's `wrap-cells`: Item Sales, the Employee Breakdown's items). Off by
   * default: a q-table without it is `q-table--no-wrap`, `white-space: nowrap` on every th and td, so a long name widens
   * its column instead of wrapping (B1215).
   */
  readonly wrapCells?: boolean;
  /**
   * How the totals row is drawn. `'summary'` (default) is legacy's ui-table-summary-row, header cells at 12 px / 500 in the
   * header ink. `'body'` is a legacy `#bottom-row` built from q-td cells (Refunds, the drawer's Orders, Payments and Voids):
   * body text, 13 px at 400, 70 % white in dark like the rows above it (B1262).
   */
  readonly totalsInk?: 'summary' | 'body';
  /**
   * Legacy's `ui-table-with-summary` (the Sales reports, Discounts, Gift Cards, Adjustments): the bottom bar has no top
   * border, so no rule closes the Total row. Other tables with totals and a pager (Refunds, the drawer's Orders and
   * Payments, Payroll Summary) keep that rule.
   */
  readonly withSummary?: boolean;
  /**
   * The last body row of a `showAll` table with no totals (B628). Quasar rules off every row but the last; the bottom
   * bar's separator closes the table. With no bar, legacy's `hide-bottom` tables leave the last row open (`'none'`, the
   * default), and its `ui-table-simple ui-table-hide-pagination` tables close it in --q-border (`'border'`, _ui.scss).
   */
  readonly lastRowRule?: 'none' | 'border';
  /**
   * Under 600 px the first column stays put while the rest scrolls (legacy BaseTable / BaseTableServerSide / UiTable
   * `stickyColumn`, default true; B1180). Pass false where legacy passes `:sticky-column="false"` or draws its table
   * without those components.
   */
  readonly stickyFirstColumn?: boolean;
  /**
   * A search in flight (legacy's TableLoaderAlternate, which OrderLookupList draws while `ordersStore.loading`, B1220): a
   * q-markup-table with the columns' headings and 10 rows, every cell a q-skeleton (rect, fade) the cell's width, and no
   * bottom bar, empty state or sticky column.
   */
  readonly loading?: boolean;
}

/** A no-data illustration in both themes (legacy's no-data-icon and no-data-icon-dark). */
export interface NoDataIllustration {
  readonly light: string;
  readonly dark: string;
}

/**
 * Legacy BaseTable's own no-data block (QTable's `no-data` slot, BaseTable.vue:518-541): the no-data icon in a 150 px square
 * (a blank square when the caller gave none, as legacy's q-img with an empty src), then 8 px under it "No Data" in small grey
 * medium text; with a search term, "No results found" and, 2 px under, Nothing matches “term” in 13 px tertiary
 * (`.ui-table-no-results__term`). The grey is legacy's `text-gray`, the q-grey token: #9da0a0 in light and dark, the AA
 * #6b6e6e only under backofficeAaLight (L143; B1422, B1555). Under 600 px legacy's QTable no-data bottom keeps
 * app.scss's 50 px all round (B1158); max-[600px] is width < 600, legacy's xs (B1183).
 */
export function TableNoData({ illustration, term, testId, size = 150, text = 'No Data' }: {
  readonly illustration?: NoDataIllustration | undefined;
  readonly term?: string | undefined;
  readonly testId?: string | undefined;
  /** BaseTable's `no-data-icon-size` (the Dashboard's lists pass 120px, B1412). */
  readonly size?: 120 | 150;
  /** BaseTable's `no-data-text` ("No sales data found" on the Dashboard, B1412). */
  readonly text?: string;
}) {
  const searching = term !== undefined && term.trim() !== '';
  const box = size === 120 ? 'h-[120px] w-[120px]' : 'h-[150px] w-[150px]';
  return (
    <div className="flex flex-col items-center pt-6 max-[600px]:p-[50px]" data-testid="table-no-data" data-table={testId}>
      {illustration === undefined ? <div className={box} data-testid="table-no-data-blank" /> : (
        <>
          <img src={illustration.light} alt="" width={size} height={size} className={`${box} object-contain dark:hidden`} data-testid="table-no-data-light" />
          <img src={illustration.dark} alt="" width={size} height={size} className={`hidden ${box} object-contain dark:block`} data-testid="table-no-data-dark" />
        </>
      )}
      {searching ? (
        <>
          <p className="pt-2 text-[14px] leading-[22px] font-medium text-grey">No results found</p>
          <p className="mt-[2px] text-[13px] leading-[19.5px] text-text-3" data-testid="table-no-data-term">{`Nothing matches “${term}”`}</p>
        </>
      ) : <p className="pt-2 text-[14px] leading-[22px] font-medium text-grey">{text}</p>}
    </div>
  );
}

/** Legacy TableLoaderAlternate's `rowCount` default. */
const SKELETON_ROWS = 10;

/**
 * Quasar's q-skeleton (type rect, animation fade): $separator-color, rgba(0,0,0,.12) (dark rgba(255,255,255,.05)), on a 4 px
 * radius, one line of the 13 px cell text tall (its `::before` is a no-break space, 19.5 px, which also gives the column legacy's
 * minimum width), fading to .4 and back every
 * 1.5 s after a .5 s delay, with the wait cursor. Measured on legacy GG LA's Order Lookup at 1600.
 */
const SKELETON = 'block rounded-[4px] bg-black/[0.12] dark:bg-white/5 cursor-wait motion-safe:animate-skeleton-fade';

export interface ServerPaging {
  /** Every row the server's filters match. */
  readonly total: number;
  readonly pageIndex: number;
  readonly pageSize: number | 'all';
  readonly onPage: (pageIndex: number, pageSize: number | 'all') => void;
}

const PER_PAGE = backofficeMetrics.table.defaultRowsPerPage;

/**
 * Body rows are divided by Quasar's q-table separator, rgba(0,0,0,.12) (#e0e0e0 on white), lighter than the header's
 * --q-border rules; in the dark theme legacy sets every table border to --q-border (lane V2, B616). Legacy's Total row
 * (ui-table-summary-row) asks for a bottom border in var(--q-gray-dark), which legacy never defines, so the row draws
 * none of its own; the rule under it is the q-table__bottom bar's top border, in the same separator colour, which
 * `ui-table-with-summary` removes and a hidden bottom (`showAll`) never draws (B614).
 */
const ROW_RULE = 'border-black/[0.12] dark:border-line';

/**
 * Legacy _ui.scss / _dark.scss `.ui-table-simple.ui-sticky-column` under 600 px (B1180): the first column sticky at the left
 * over the scrolling rest, ruled 1 px on its right in --q-border. Its header cell is white (dark: the 06dp surface), its body
 * cells #fafbfc (dark: 06dp). Legacy fills the dark summary cell with --q-brand-primary (#b4d5ac) under 87 % white text, about
 * 1.5:1 (legacy bug B1206, FIX L973); Next keeps the 06dp surface there.
 */
// The rule is an inset shadow where legacy draws a right border; the extra 1 px of right padding stands for that border
// inside the cell's box. (It was a shadow because a collapsed table's borders stayed behind when the sticky cell moved;
// since B1226 the table's borders are separate, as legacy's q-table, and each cell carries its own row rule.)
const STICKY = 'max-[600px]:sticky max-[600px]:left-0 max-[600px]:z-[1] max-[600px]:pr-[17px] max-[600px]:shadow-[inset_-1px_0_0_var(--color-line)]';
const STICKY_HEAD = `${STICKY} max-[600px]:bg-sticky-head`;
const STICKY_BODY = `${STICKY} max-[600px]:bg-sticky-cell`;
/** The same first column for a table drawn by hand (Reports › Breaks, B1960): its header cell, then its body cells. */
export const STICKY_FIRST_HEAD = STICKY_HEAD;
export const STICKY_FIRST_CELL = STICKY_BODY;

/**
 * Quasar's row hover (QTable.sass: `tbody td:before`, an absolute overlay on every cell, given content on a hovered row;
 * legacy _light.scss tints it rgb(0 0 0 / 6%)). It is drawn per cell, not on the row, so it also tints the pinned first
 * cell over its own fill under 600 px (B1214). The row carries `group/row`.
 */
/**
 * A focusable row's keyboard ring (rowKeys): legacy's `q-tr tabindex="0"` shows the browser's own ring (outline auto 1 px,
 * offset 0), a full box round the row. The app's 2 px ring at offset 2 fell outside the row, where the table's scroll box
 * cut it to its top and bottom lines (B1348).
 */
const ROW_FOCUS = 'focus-visible:[outline:auto_1px] focus-visible:outline-offset-0';

const CELL_HOVER = "relative before:pointer-events-none before:absolute before:inset-0 before:content-[''] group-hover/row:before:bg-row-hover";

const blank = (cell: ReactNode): boolean => cell === '' || cell === null || cell === undefined;

/**
 * Legacy BaseTable with no visible row but a `bottom-row` (B1446): the totals as a header-less row over the no-data block.
 * The leading blank cells are legacy's one `colspan` cell and trailing blanks are dropped, as the drawer tables' slots
 * write them, so the browser lays the few cells out as legacy's do.
 */
function NoDataTotals({ totals, illustration, term, testId }: { readonly totals: readonly ReactNode[]; readonly illustration: NoDataIllustration | undefined; readonly term: string | undefined; readonly testId: string | undefined }) {
  const lead = Math.max(0, totals.findIndex((cell) => !blank(cell)));
  let end = totals.length;
  while (end > lead && blank(totals[end - 1])) end -= 1;
  return (
    <div data-testid={testId}>
      <table className="w-full border-separate border-spacing-0 text-[13px]">
        <tbody>
          <tr data-testid="table-totals">
            {lead > 0 ? <td colSpan={lead} className="px-4 py-[7px]" /> : null}
            {totals.slice(lead, end).map((cell, i) => (
              <td key={`total-${String(lead + i)}`} className="py-0 pr-0 pl-1 text-left text-text-1 dark:text-text-2"><div className="px-4 py-2 text-right">{cell}</div></td>
            ))}
          </tr>
        </tbody>
      </table>
      <TableNoData illustration={illustration} term={term} testId={testId} />
    </div>
  );
}

export function DataTable<T>({ columns, data, getRowId, initialSort = [], onRowClick, rowKeys = false, empty, totals, noDataTotals = false, showAll = false, testId, caption, subRows, serverPaging, legacyServerSide = false, rowHover = false, subRowHover = false, wrapCells = false, withSummary = false, lastRowRule = 'none', stickyFirstColumn: sticky = true, loading = false, totalsInk = 'summary', noDataIllustration, noDataTerm }: DataTableProps<T>) {
  const stickyFirstColumn = sticky && !loading;
  const wrap = wrapCells ? '' : 'whitespace-nowrap';
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [ownPageSize, setOwnPageSize] = useState<number>(showAll ? Number.MAX_SAFE_INTEGER : PER_PAGE);
  const [ownPageIndex, setOwnPageIndex] = useState(0);
  const server = serverPaging;
  const pageSize = server === undefined ? ownPageSize : server.pageSize === 'all' ? Math.max(server.total, 1) : server.pageSize;
  const pageIndex = server === undefined ? ownPageIndex : server.pageIndex;
  const setPageIndex = (i: number) => (server === undefined ? setOwnPageIndex(i) : server.onPage(i, server.pageSize));
  const setPageSize = (size: number) => (server === undefined ? setOwnPageSize(size) : server.onPage(0, size === Number.MAX_SAFE_INTEGER ? 'all' : size));
  const table = useReactTable<T>({
    data: data as T[],
    columns,
    getRowId,
    state: { sorting, expanded, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    // Quasar's q-table sorts every column ascending first (then descending, then unsorted); TanStack would start a
    // numeric column descending (B637).
    sortDescFirst: false,
    onExpandedChange: setExpanded,
    getRowCanExpand: (row) => (subRows?.(row.original).length ?? 0) > 0,
    // Detail rows are drawn by the table, not as TanStack sub-rows, so paging counts the parent rows only.
    paginateExpandedRows: false,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      if (next.pageSize !== pageSize) setPageSize(next.pageSize);
      else setPageIndex(next.pageIndex);
    },
    ...(server === undefined ? {} : { manualPagination: true, rowCount: server.total }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  const total = server === undefined ? data.length : server.total;
  const rows = table.getRowModel().rows;
  // A row's rule, drawn by each of its cells (a separate-border table draws no row borders; B1226). Quasar rules
  // `tbody tr:not(:last-child)`, so the table's last row (an expanded row's last detail row) has none of its own: under a
  // bottom bar the bar's top border draws it, below the row's height as legacy's (44 + 1), and with no bar and no totals it
  // follows `lastRowRule`.
  const closing = (last: boolean): string => {
    if (!last || totals !== undefined) return `border-b ${ROW_RULE}`;
    if (!showAll) return '';
    return lastRowRule === 'border' ? 'border-b border-line' : '';
  };
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(total, (pageIndex + 1) * pageSize);
  const pages = table.getPageCount();

  if (!loading && total === 0 && empty !== undefined) return <>{empty}</>;
  // Legacy BaseTable hides its header once no row is visible (`:hide-header="hideHeaders || !hasVisibleRows"`, BaseTable.vue:373)
  // and QTable's no-data slot takes the bar's place, so a caller without `empty` gets that block, never a bare header over
  // "Displaying 0-0 of 0" (B1325). A Total row keeps the table, and B1323's borderless bar under it.
  if (!loading && total === 0 && totals === undefined) return <TableNoData illustration={noDataIllustration} term={noDataTerm} testId={testId} />;
  if (!loading && total === 0 && totals !== undefined && noDataTotals) return <NoDataTotals totals={totals} illustration={noDataIllustration} term={noDataTerm} testId={testId} />;

  return (
    <div data-testid={testId} aria-busy={loading || undefined}>
      {/* `relative` holds a header's sr-only label (Order Lookup's View) inside the scroller: an absolute box escapes an
          unpositioned overflow container, and at 390 the label 600 px to the right let the whole page scroll sideways (B1222). */}
      <div className="relative overflow-x-auto" data-testid="table-scroll">
        {/* Body cells 13 px: Quasar's $table-tbody-td-font-size, which legacy's q-tables keep. */}
        <table className="w-full border-separate border-spacing-0 text-[13px]">
          {caption === undefined ? null : <caption className="sr-only">{caption}</caption>}
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="h-11 bg-gray-alt dark:bg-transparent">
                {group.headers.map((header, h) => {
                  const meta = layoutOf(header.column.columnDef.meta);
                  const sorted = header.column.getIsSorted();
                  const label = flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={meta?.width === undefined || loading ? undefined : { width: meta.width }}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                      className={`border-y border-line px-4 text-[12px] font-medium ${wrapCells ? 'py-[7px]' : wrap} text-text-1 capitalize ${header.column.getCanSort() ? 'group' : ''} ${alignClass(meta?.align)} ${stickyFirstColumn && h === 0 ? STICKY_HEAD : ''}`}
                    >
                      {/* A button resets text-transform, so a sortable label capitalises itself ("Cash (In Hand)", as legacy's headers read). */}
                      {header.column.getCanSort() ? (
                        <button type="button" onClick={header.column.getToggleSortingHandler()} className={wrapCells ? 'font-medium capitalize' : 'inline-flex items-center align-middle font-medium capitalize'}>
                          {meta?.align === 'right' ? <SortIcon sorted={sorted} align="right" /> : null}
                          {label}
                          {meta?.align === 'right' ? null : <SortIcon sorted={sorted} align={meta?.align} />}
                        </button>
                      ) : label}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {/* Legacy's q-table is `border-collapse: separate` with no spacing: each cell carries its rules inside its own
              box, so a 44 px row is 44 px with its rule and row N starts N rows down. Collapsed, each rule added half a pixel
              to its row and the rows drifted from legacy's by about a pixel every two rows (B1226). Rows draw no borders in
              this model, so every rule is on the cells. */}
          <tbody>
            {loading ? Array.from({ length: SKELETON_ROWS }, (_, r) => (
              <tr key={`skeleton-${String(r)}`} data-testid="table-skeleton-row" className="h-11">
                {table.getVisibleLeafColumns().map((column) => (
                  <td key={column.id} className={`border-b ${ROW_RULE} px-4 py-[7px] text-left`}><span className={`h-[19.5px] w-full ${SKELETON}`}>{' '}</span></td>
                ))}
              </tr>
            )) : rows.map((row, index) => {
              const rule = closing(index === rows.length - 1 && !row.getIsExpanded());
              const details = row.getIsExpanded() ? (subRows?.(row.original) ?? []) : [];
              return (
              <Fragment key={row.id}>
                <tr
                  data-testid="table-row"
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row.original)}
                  tabIndex={onRowClick !== undefined && rowKeys ? 0 : undefined}
                  onKeyDown={onRowClick === undefined || !rowKeys ? undefined : (e) => {
                    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                    e.preventDefault();
                    onRowClick(row.original);
                  }}
                  className={`h-11 ${rowHover ? 'group/row' : ''} ${onRowClick === undefined ? '' : 'cursor-pointer'} ${onRowClick !== undefined && rowKeys ? ROW_FOCUS : ''}`}
                >
                  {/* Legacy's q-table body cells are 70 % white in dark under its 87 % headers and summary row (B1210). */}
                  {row.getVisibleCells().map((cell, c) => {
                    const onCellClick = layoutOf(cell.column.columnDef.meta)?.onCellClick;
                    return (
                      <td
                        key={cell.id}
                        onClick={onCellClick === undefined ? undefined : (e) => { if (!pressedControl(e.target, e.currentTarget)) onCellClick(row.original); }}
                        className={`${layoutOf(cell.column.columnDef.meta)?.iconLeft === true ? 'py-0 pr-0 pl-1' : 'px-4 py-[7px]'} ${rule} text-text-1 dark:text-text-2 ${wrap} ${alignClass(layoutOf(cell.column.columnDef.meta)?.align)} ${stickyFirstColumn && c === 0 ? STICKY_BODY : ''} ${rowHover ? CELL_HOVER : ''} ${onCellClick === undefined ? '' : 'cursor-pointer'}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
                {details.map((cells, i) => {
                  const detailRule = closing(index === rows.length - 1 && i === details.length - 1);
                  return (
                    <tr key={`${row.id}-sub-${String(i)}`} data-testid="table-subrow" className={`h-11 ${subRowHover ? 'group/row' : ''}`}>
                      {row.getVisibleCells().map((cell, c) => (
                        <td key={cell.id} className={`${detailRule} px-4 py-[7px] text-text-3 italic ${wrap} ${c === 0 ? 'pl-12' : ''} ${alignClass(layoutOf(cell.column.columnDef.meta)?.align)} ${stickyFirstColumn && c === 0 ? STICKY_BODY : ''} ${subRowHover ? CELL_HOVER : ''}`}>
                          {cells[c]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </Fragment>
              );
            })}
          </tbody>
          {totals === undefined || loading ? null : (
            <tfoot>
              {/* Legacy's summary row (ui-table-summary-row) is header cells: 12 px at weight 500, not bold, padded 7 px 16 px (a two-line Payroll Summary total is 52 px tall). */}
              <tr className={`h-11 ${totalsInk === 'body' ? '' : 'text-[12px] font-medium'}`} data-testid="table-totals">
                {table.getVisibleLeafColumns().map((column, i) => (
                  <td key={column.id} className={`px-4 py-[7px] text-text-1 ${wrap} ${totalsInk === 'body' ? 'dark:text-text-2' : ''} ${alignClass(layoutOf(column.columnDef.meta)?.align)} ${stickyFirstColumn && i === 0 ? STICKY_BODY : ''}`}>{totals[i]}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showAll || loading ? null : (
        // Legacy's q-table__bottom: 50 px tall at least, 24 px of it padding over the row (pp-table), its items centred, so
        // "Displaying" sits 28 px under the table, and 24 px plus half the 32 px pager row once the picker shows (B1355). Its 1 px top
        // border, inside those 50 px, is the rule under the last row or the Total row; `ui-table-with-summary` removes it (B614).
        // With no rows the bar shows only under a Total row (the no-data block takes the table's place otherwise, B1325), and
        // keeps that rule: Quasar 2.18.6 QTable.sass:244 borders `.q-table__bottom:not(.q-table__bottom--nodata)`, and the
        // borderless nodata bottom is the no-data block's (legacy _ui.scss:59 agrees; B1323).
        // Under 600 legacy drops pp-table's 24 px padding for Quasar's own (4 px, 16 left and 14 right) and its
        // pp-pagination-wrapper is a centred column 0.5em down: "Displaying" centred 11 px under the row, and the pager a
        // full-width row 1em under it with Records per page and the picker at the start and the buttons at the end (B1341).
        <div className={`flex min-h-[50px] flex-col items-center pt-1 pr-[14px] pb-1 pl-4 min-[600px]:flex-row min-[600px]:flex-wrap min-[600px]:justify-between min-[600px]:gap-2 min-[600px]:px-0 min-[600px]:pt-6 min-[600px]:pb-0 ${withSummary ? '' : `border-t ${ROW_RULE}`} text-[12px] text-text-1 dark:text-text-2`} data-testid="table-bottom">
          {/* Dark: legacy's q-table__bottom words are rgba(255, 255, 255, 0.7), as the rows' (B1359). */}
          <span data-testid="table-records" className="mt-1.5 min-[600px]:mt-0">{`Displaying ${from}-${to} of ${total} Records`}</span>
          {total > PER_PAGE ? (
            <div data-testid="table-pager" className="mt-3 flex w-full items-center justify-between gap-3 min-[600px]:mt-0 min-[600px]:w-auto min-[600px]:justify-start">
              {/* Legacy's pp-pagination--button-wrapper: 1em between the label, the picker and the buttons at every width (B1355). */}
              <div className="flex items-center gap-3">
                <span>Records per page</span>
                <SelectField
                  label="Records per page"
                  inlineLabel
                  dense
                  value={server !== undefined ? String(server.pageSize) : pageSize === Number.MAX_SAFE_INTEGER ? 'all' : String(pageSize)}
                  options={[{ value: '30', label: '30' }, { value: '50', label: '50' }, { value: 'all', label: 'All' }]}
                  onValueChange={(v) => {
                    if (server !== undefined) { server.onPage(0, v === 'all' ? 'all' : Number(v)); return; }
                    setOwnPageIndex(0); setOwnPageSize(v === 'all' ? Number.MAX_SAFE_INTEGER : Number(v));
                  }}
                  pager={server !== undefined || legacyServerSide ? 'server' : 'client'}
                />
              </div>
              <div className="flex items-center gap-2">
                {pages > 2 ? <PagerButton label="First page" icon="first" onClick={() => setPageIndex(0)} disabled={pageIndex === 0} /> : null}
                <PagerButton label="Previous page" icon="previous" onClick={() => setPageIndex(pageIndex - 1)} disabled={pageIndex === 0} />
                <PagerButton label="Next page" icon="next" onClick={() => setPageIndex(pageIndex + 1)} disabled={pageIndex >= pages - 1} />
                {pages > 2 ? <PagerButton label="Last page" icon="last" onClick={() => setPageIndex(pages - 1)} disabled={pageIndex >= pages - 1} /> : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Material Icons' first_page, chevron_left, chevron_right and last_page: legacy's pager q-btn icons. */
const PAGER_ICON = {
  first: 'M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z',
  previous: 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z',
  next: 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
  last: 'M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z',
} as const;

/**
 * Legacy BaseTable's pager buttons: dense outline q-btns, 32 px with 8 px corners and a 1 px --q-border outline (dark's
 * #373739 over the 12dp field fill), a 24 px Material icon in $gray in both themes, 0.7 opaque while disabled (B1355,
 * measured beside GG LA's Menu > Items).
 */
function PagerButton({ label, icon, onClick, disabled }: { readonly label: string; readonly icon: keyof typeof PAGER_ICON; readonly onClick: () => void; readonly disabled: boolean }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} disabled={disabled} className="inline-flex size-8 items-center justify-center cursor-pointer rounded-q border border-line text-grey dark:bg-field disabled:cursor-not-allowed disabled:opacity-70">
      <svg aria-hidden width={24} height={24} viewBox="0 0 24 24"><path d={PAGER_ICON[icon]} fill="currentColor" /></svg>
    </button>
  );
}

/**
 * Table search (legacy BaseTable `tableFilter`, with B759 fixed, DT-76): a one-character term matches word initials,
 * as legacy; a longer term matches when the whole name contains it as typed, ignoring case. Legacy used the term as a
 * regular expression tested word by word, so "Iced Tea" never matched and "(" matched nothing.
 */
export function legacySearchMatches(term: string, name: string): boolean {
  const trimmed = term.trim().toLowerCase();
  if (trimmed === '') return true;
  if (trimmed.length === 1) return name.split(' ').some((w) => w.toLowerCase().startsWith(trimmed));
  return name.toLowerCase().includes(trimmed);
}

type ChipTone = 'active' | 'info' | 'neutral' | 'negative';
const CHIP: Readonly<Record<ChipTone, string>> = {
  active: 'border-positive text-positive-ink bg-chip-active',
  // Legacy `ui-chip-info` (b4bfde902): progress rather than an outcome, a swap the coworker accepted that waits on a manager.
  // The words in device-chip-info-ink: legacy's #326ae4 is 4.4:1 on #eff3fd, so the AA theme darkens it (#2c5fd0; dark #93d0ff).
  info: 'border-[var(--q-brand-blue)] text-device-chip-info-ink bg-[var(--q-brand-blue-lighter)] dark:bg-transparent',
  neutral: 'border-line text-text-3 bg-chip-neutral',
  negative: 'border-negative text-negative bg-chip-negative',
};

/**
 * W22 TableChipIndicator: 9 px / 600 uppercase text on legacy's 13.5 px line with 0.25 px tracking in a 12 px-radius
 * outlined chip, 23.5 px tall (B811). `point`: legacy `add-point`, a 12 px
 * dot in the chip's colour before the words; legacy's inactive dot has no size, so an off chip keeps only its 5 px gap.
 */
export function ChipIndicator({ tone, children, point = false }: { readonly tone: ChipTone; readonly children: ReactNode; readonly point?: boolean }) {
  return (
    <span className={`inline-flex items-center rounded-chip border px-2 py-1 text-[9px] leading-[13.5px] tracking-[0.25px] font-semibold uppercase ${point ? 'gap-[5px]' : ''} ${CHIP[tone]}`}>
      {point ? <span aria-hidden data-testid="chip-point" className={tone === 'neutral' ? '' : 'size-3 rounded-full bg-current'} /> : null}
      {children}
    </span>
  );
}
